import React, { Suspense } from "react";
import { Image, Button, Spinner, Kbd, Link } from "@heroui/react";
import { IconX, IconCards, IconChartPie } from "@tabler/icons-react";
import { useCardDBInfiniteScroll } from "@/components/DeckBuilder/hooks/useCardDBInfiniteScroll";
import { SortableRect } from "@/components/DeckBuilder/SortableRect";
import { Droppable } from "@/components/dnd/Droppable";
import Export from "@/components/DeckBuilder/Mobile/Export";
import LAYOUTS from "@/components/DeckBuilder/layouts";
import SimpleBarReact from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import "@/styles/scrollbar.css";
import { useCurrentGame } from "@/stores/deckStore";

import SpecFilters from "@/components/DeckBuilder/filters/SpecFilters";
import { getLegacyFilterBar } from "@/components/DeckBuilder/filters/registry";
import { getGameHandler } from "@/games";
import { CDN } from "@/lib/cdn";

const DeckLayoutBottom = React.memo(function DeckLayoutBottom({
  viewMode,
  deck,
  getCount,
  copyDeck,
  filters,
  setFilter,
  clearAllFilters,
  deckCardsMap,
  displayCards,
  handleOpen,
  setLayout,
  hasNextPage,
  fetchNextPage,
  page,
  clearNewDeck,
  setHoveredAACardId,
  searchDisplay,
  setSearchDisplay,
}: {
  viewMode: string;
  deck: any;
  getCount: any;
  copyDeck: any;
  filters: any;
  setFilter: any;
  clearAllFilters: any;
  deckCardsMap: any;
  displayCards: any;
  handleOpen: any;
  setLayout: any;
  hasNextPage: any;
  fetchNextPage: any;
  page: number;
  clearNewDeck: any;
  setHoveredAACardId: any;
  searchDisplay: any;
  setSearchDisplay: any;
}) {
  const game = useCurrentGame();
  const { scrollRef, sentinelRef } = useCardDBInfiniteScroll({
    hasNextPage,
    fetchNextPage,
    resetOnChange: filters,
    page,
  });

  // useEffect(() => {
  //   const deckItems = deck.get();
  //   const deckIdsSet = new Set(deckItems.map((item: DeckItem) => item.id));

  //   setCardOrderIds((prevCardOrderIds: string[]) => {
  //     // Filter out ids that no longer exist in the deck
  //     const filteredItems = prevCardOrderIds.filter((id) => deckIdsSet.has(id));

  //     // Add new ids from the deck that are not already in items
  //     const newIds = deckItems
  //       .map((item: DeckItem) => item.id)
  //       .filter((id: string) => !filteredItems.includes(id));

  //     // Preserve the order by concatenating new IDs at the end
  //     return [...filteredItems, ...newIds];
  //   });
  // }, [deck.get().length]);

  // let newDisplayDeck = [];
  // for (let i = 0; i < deck.get().length; i++) {
  //   const card = deck.get()[i];
  //   const imgUrl = getCardImage(card.id);
  //   newDisplayDeck.push(

  //   );
  // }
  // Which filters a game shows is the game's own declaration, not a branch
  // here: a game with a spec gets a bar rendered from it, and the games that
  // predate specs register their bespoke bar in their config.
  const handler = getGameHandler(game);
  const filterSpec = handler.getFilterSpec?.();
  const FiltersComponent = getLegacyFilterBar(game);

  const filterProps = {
    filters,
    setFilter,
    searchDisplay,
    setSearchDisplay,
    deckCardsMap,
    clearAllFilters,
  };

  let allFilters = null;
  if (filterSpec) {
    allFilters = (
      <SpecFilters
        spec={filterSpec}
        cards={Object.values(
          handler.getDeckBuilderCardFunctions().cleanCardData(),
        )}
        {...filterProps}
      />
    );
  } else if (FiltersComponent) {
    allFilters = <FiltersComponent {...filterProps} />;
  }

  return (
    <div>
      {/* <div>Scroll Top: {scrollTop}</div> */}
      <div className="">
        <div className="">
          <div className="flex flex-row items-center">
            <div className="min-w-[50px] min-h-[50px] mr-2 mt-3">
              <Link href="/">
                <Image
                  src={`${CDN}/shared/logo/gumgum.png`}
                  loading="eager"
                  alt="gumgum.gg OPTCG fan site"
                  height={50}
                  width={50}
                  className="sm:mb-2"
                />
              </Link>
            </div>
            {allFilters}
          </div>
          <Suspense
            fallback={
              <div>
                <Spinner />
              </div>
            }
          >
            <div
              ref={scrollRef}
              className="overflow-y-auto overflow-x-hidden"
              style={{ height: "calc(57.5vh)", maxWidth: "100%" }}
            >
              <div
                id="card-db"
                className="grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-0 pt-3 px-2 pb-2"
              >
                {displayCards}
              </div>
              {hasNextPage() && (
                <div
                  ref={sentinelRef}
                  className="flex justify-center py-4 min-h-[48px]"
                >
                  <Spinner size="sm" />
                </div>
              )}
            </div>
          </Suspense>
        </div>
      </div>

      {/* deck */}
      <div>
        <div className="bg-gray-950 rounded-md mt-2">
          <div className="flex items-center gap-2 p-3">
            <div className="flex flex-row gap-2 w-[100px]">
              <IconCards />
              {getCount()} cards
            </div>
            {viewMode === "d" && (
              <Button
                size="sm"
                variant="ghost"
                color="primary"
                onPress={() => copyDeck(true)}
              >
                Copy
              </Button>
            )}
            <Button
              size="sm"
              color="danger"
              onPress={() => clearNewDeck()}
              className="text-sm"
              startContent={<IconX height={18} width={18} />}
            >
              Clear
            </Button>
            {/* <Button
              size="sm"
              isIconOnly
              variant="ghost"
              color="primary"
              onPress={() => setLayout("side")}
            >
              <IconLayoutSidebar />
            </Button> */}
            <Button
              size="sm"
              color="primary"
              onPress={() => setLayout(LAYOUTS.DECK)}
              startContent={<IconChartPie width={18} height={18} />}
              className="text-sm"
            >
              Deck Stats
            </Button>
            <div className="ml-auto w-[400px]">
              <Export deck={deck} />
            </div>
          </div>
          <Droppable id="display-deck">
            <div
              className="overflow-y-auto overflow-x-hidden"
              style={{ height: "calc(50vh - 240px)", minHeight: "120px" }}
            >
              <div className="flex flex-col gap-2 h-full">
                {getCount() <= 0 ? (
                  <div className="p-3 flex flex-col w-full justify-center items-center text-slate-400">
                    <div className="flex flex-col gap-2">
                      <div className="not-italic text-lg font-semibold">
                        Deckbuilder Tips & Tricks
                      </div>
                      <div>1. Drag and drop cards to add or sort</div>
                      <div>
                        2. Hover a card and press <Kbd>0</Kbd>, <Kbd>1</Kbd>,{" "}
                        <Kbd>2</Kbd>, <Kbd>3</Kbd>, <Kbd>4</Kbd> to set that
                        number of cards
                      </div>
                      <div>
                        3. Hover a card and press <Kbd>i</Kbd> to inspect
                      </div>
                      <div>
                        4. Use <Kbd keys={["command"]}>K</Kbd> to focus search
                        bar
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-3 p-3 px-1">
                    <SortableRect
                      handleOpen={handleOpen}
                      setHoveredAACardId={setHoveredAACardId}
                    />
                  </div>
                )}
              </div>
            </div>
          </Droppable>
        </div>
      </div>
    </div>
  );
});

export default DeckLayoutBottom;
